import React, { useEffect, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "@/components/Icon";
import { Modal } from "@/components/Modal";

export interface LightboxItem {
    id?: number | string;
    url?: string;
    title: string;
    isBlob?: boolean;
    loadBlob?: () => Promise<Blob>;
    canDelete?: boolean;
}

export interface ImageLightboxModalProps {
    isOpen: boolean;
    items: LightboxItem[];
    initialIndex?: number;
    initialId?: number | string;
    onClose: () => void;
    onDelete?: (item: LightboxItem, index: number) => void;
    headerActions?: (item: LightboxItem) => React.ReactNode;
}

export const isImageFile = (filename: string, mimeType?: string): boolean => {
    if (mimeType && mimeType.startsWith("image/")) {
        return true;
    }

    return /\.(jpe?g|png|gif|webp|svg|bmp|ico|avif)$/i.test(filename);
};

export const getItemKey = (item?: LightboxItem, index: number = 0): string => {
    if (!item) {
        return `idx_${index}`;
    }
    if (item.id !== undefined && item.id !== null) {
        return `id_${item.id}`;
    }
    if (item.url) {
        return `url_${item.url}`;
    }
    return `idx_${index}_${item.title}`;
};

export const ImageLightboxModal: React.FC<ImageLightboxModalProps> = ({
    isOpen,
    items,
    initialIndex = 0,
    initialId,
    onClose,
    onDelete,
    headerActions,
}) => {
    const { t } = useTranslation("global");

    const resolvedInitialIndex = React.useMemo(() => {
        if (initialId !== undefined && initialId !== null) {
            const found = items.findIndex(
                (it) => String(it.id) === String(initialId)
            );
            if (found >= 0) {
                return found;
            }
        }
        return initialIndex >= 0 && initialIndex < items.length
            ? initialIndex
            : 0;
    }, [initialId, initialIndex, items]);

    const [currentIndex, setCurrentIndex] =
        useState<number>(resolvedInitialIndex);
    const [loadedUrls, setLoadedUrls] = useState<Record<string, string>>({});
    const [loadingMap, setLoadingMap] = useState<Record<string, boolean>>({});
    const [errorMap, setErrorMap] = useState<Record<string, string>>({});
    const [pendingDelete, setPendingDelete] = useState<LightboxItem | null>(
        null
    );

    const prevOpenRef = useRef<boolean>(false);
    const createdUrlsRef = useRef<string[]>([]);
    const loadedUrlsRef = useRef<Record<string, string>>({});
    const loadingInProgressRef = useRef<Set<string>>(new Set());

    // Synchronize current index when opened or resolved index changes
    useEffect(() => {
        if (isOpen) {
            setCurrentIndex(resolvedInitialIndex);
        }
        prevOpenRef.current = isOpen;
    }, [isOpen, resolvedInitialIndex]);

    const cleanup = useCallback((): void => {
        if (typeof document !== "undefined") {
            (document.activeElement as HTMLElement | null)?.blur();
        }
        createdUrlsRef.current.forEach((url) => {
            try {
                URL.revokeObjectURL(url);
            } catch {}
        });
        createdUrlsRef.current = [];
        loadedUrlsRef.current = {};
        loadingInProgressRef.current.clear();
        setLoadedUrls({});
        setLoadingMap({});
        setErrorMap({});
        setPendingDelete(null);
    }, []);

    const handleClose = useCallback((): void => {
        if (typeof document !== "undefined") {
            (document.activeElement as HTMLElement | null)?.blur();
        }
        cleanup();
        onClose();
    }, [cleanup, onClose]);

    const prevImage = useCallback((): void => {
        setCurrentIndex((prev) => (prev > 0 ? prev - 1 : items.length - 1));
    }, [items.length]);

    const nextImage = useCallback((): void => {
        setCurrentIndex((prev) => (prev < items.length - 1 ? prev + 1 : 0));
    }, [items.length]);

    const loadItem = useCallback(
        async (index: number): Promise<void> => {
            const item = items[index];
            if (!item) {
                return;
            }

            const key = getItemKey(item, index);

            if (item.url) {
                return;
            }

            if (loadedUrlsRef.current[key]) {
                return;
            }

            if (loadingInProgressRef.current.has(key)) {
                return;
            }

            if (!item.loadBlob) {
                return;
            }

            loadingInProgressRef.current.add(key);
            setLoadingMap((prev) => ({ ...prev, [key]: true }));
            setErrorMap((prev) => {
                if (!prev[key]) {
                    return prev;
                }
                const next = { ...prev };
                delete next[key];
                return next;
            });

            try {
                const blob = await item.loadBlob();
                const url = URL.createObjectURL(blob);
                createdUrlsRef.current.push(url);
                loadedUrlsRef.current[key] = url;
                setLoadedUrls((prev) => ({ ...prev, [key]: url }));
            } catch (err: unknown) {
                console.error(
                    `Error loading image in lightbox at index ${index}:`,
                    err
                );
                setErrorMap((prev) => ({
                    ...prev,
                    [key]: t("lightbox.error_image"),
                }));
            } finally {
                loadingInProgressRef.current.delete(key);
                setLoadingMap((prev) => ({ ...prev, [key]: false }));
            }
        },
        [items, t]
    );

    useEffect(() => {
        if (!isOpen) {
            cleanup();
            return;
        }

        if (items.length === 0) {
            return;
        }

        const validIndex =
            currentIndex >= 0 && currentIndex < items.length
                ? currentIndex
                : resolvedInitialIndex;
        void loadItem(validIndex);

        if (items.length > 1) {
            const nextIdx = (validIndex + 1) % items.length;
            void loadItem(nextIdx);

            if (items.length > 2) {
                const prevIdx = (validIndex - 1 + items.length) % items.length;
                void loadItem(prevIdx);
            }
        }
    }, [
        isOpen,
        currentIndex,
        items.length,
        resolvedInitialIndex,
        loadItem,
        cleanup,
    ]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const handleKeyDown = (e: KeyboardEvent): void => {
            switch (e.key) {
                case "Escape":
                    handleClose();
                    break;
                case "ArrowLeft":
                    prevImage();
                    break;
                case "ArrowRight":
                    nextImage();
                    break;
                default:
                    break;
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, handleClose, prevImage, nextImage]);

    const confirmDelete = (): void => {
        if (!pendingDelete || !onDelete) {
            return;
        }
        const index = items.findIndex(
            (item) =>
                item.id === pendingDelete.id &&
                item.title === pendingDelete.title
        );
        onDelete(pendingDelete, index >= 0 ? index : currentIndex);
        setPendingDelete(null);
    };

    if (!isOpen || items.length === 0 || typeof document === "undefined") {
        return null;
    }

    const safeIndex =
        currentIndex >= 0 && currentIndex < items.length ? currentIndex : 0;
    const currentItem = items[safeIndex];
    const currentKey = getItemKey(currentItem, safeIndex);
    const currentUrl =
        currentItem?.url ||
        (currentKey
            ? loadedUrls[currentKey] || loadedUrlsRef.current[currentKey]
            : "");
    const currentError = currentKey ? errorMap[currentKey] : undefined;

    return createPortal(
        <>
            <div
                className="fixed inset-0 z-[110000] bg-black/90 backdrop-blur-md flex flex-col justify-between select-none animate-fade-in"
                onClick={handleClose}
            >
                <div
                    className="flex items-center justify-between px-6 py-4 z-20 text-white"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="flex items-center gap-3 min-w-0">
                        <span className="text-sm font-bold tracking-wide truncate max-w-[280px] sm:max-w-[450px]">
                            {currentItem?.title}
                        </span>
                        {items.length > 1 && (
                            <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-white/20 text-white/90 shrink-0">
                                {safeIndex + 1} / {items.length}
                            </span>
                        )}
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                        {headerActions &&
                            currentItem &&
                            headerActions(currentItem)}
                        {onDelete &&
                            currentItem &&
                            (currentItem.canDelete ?? true) && (
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setPendingDelete(currentItem);
                                    }}
                                    className="p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer"
                                    title={t("modal.delete")}
                                >
                                    <Icon
                                        value="trash"
                                        size={18}
                                        className="text-white"
                                    />
                                </button>
                            )}
                        {currentUrl && (
                            <a
                                href={currentUrl}
                                download={currentItem?.title}
                                className="p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer"
                                title={t("lightbox.download_image")}
                            >
                                <Icon
                                    value="download"
                                    size={18}
                                    className="text-white"
                                />
                            </a>
                        )}
                        <button
                            type="button"
                            onClick={handleClose}
                            className="p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer"
                            title={t("lightbox.close")}
                        >
                            <Icon value="x" size={20} className="text-white" />
                        </button>
                    </div>
                </div>

                <div
                    className="relative flex-1 flex items-center justify-center p-4 z-10 overflow-hidden"
                    onClick={handleClose}
                >
                    {items.length > 1 && (
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                prevImage();
                            }}
                            className="absolute left-4 md:left-8 p-3 rounded-full bg-black/50 hover:bg-black/80 text-white border border-white/20 backdrop-blur-xs transition-all cursor-pointer z-30 shadow-lg"
                            title={t("lightbox.prev_image")}
                        >
                            <Icon value="chevronLeft" size={24} />
                        </button>
                    )}

                    {currentError ? (
                        <div
                            className="flex flex-col items-center gap-3 text-rose-400 p-6 max-w-md text-center bg-black/40 rounded-2xl border border-rose-500/20"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <Icon
                                value="alertCircle"
                                size={40}
                                className="text-rose-400"
                            />
                            <p className="text-sm font-medium text-rose-200">
                                {currentError}
                            </p>
                            <button
                                type="button"
                                onClick={() => void loadItem(safeIndex)}
                                className="mt-2 px-4 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-semibold cursor-pointer transition-colors"
                            >
                                {t("modal.retry")}
                            </button>
                        </div>
                    ) : currentUrl ? (
                        <img
                            src={currentUrl}
                            alt={currentItem?.title}
                            onClick={(e) => e.stopPropagation()}
                            className="max-h-[82vh] max-w-[90vw] object-contain rounded-xl shadow-2xl transition-all cursor-default"
                        />
                    ) : (
                        <div className="flex flex-col items-center gap-3 text-white/80">
                            <Icon
                                value="loader"
                                size={32}
                                className="animate-spin text-white"
                            />
                            <span className="text-xs">
                                {t("lightbox.loading_image")}
                            </span>
                        </div>
                    )}

                    {items.length > 1 && (
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                nextImage();
                            }}
                            className="absolute right-4 md:right-8 p-3 rounded-full bg-black/50 hover:bg-black/80 text-white border border-white/20 backdrop-blur-xs transition-all cursor-pointer z-30 shadow-lg"
                            title={t("lightbox.next_image")}
                        >
                            <Icon value="chevronRight" size={24} />
                        </button>
                    )}
                </div>

                {items.length > 1 && (
                    <div
                        className="flex items-center justify-center gap-1.5 py-3 z-10"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {items.map((item, idx) => (
                            <button
                                key={`${item.title}-${idx}`}
                                type="button"
                                onClick={() => setCurrentIndex(idx)}
                                className={`h-2 rounded-full transition-all cursor-pointer ${
                                    idx === safeIndex
                                        ? "w-6 bg-white"
                                        : "w-2 bg-white/40 hover:bg-white/60"
                                }`}
                                title={item.title}
                            />
                        ))}
                    </div>
                )}
            </div>

            {onDelete && (
                <Modal
                    isOpen={Boolean(pendingDelete)}
                    onClose={() => setPendingDelete(null)}
                    title={t("developments.detail.delete_attachment_title")}
                    maxWidth="sm"
                    submitVariant="danger"
                    onSubmit={confirmDelete}
                    submitLabel={t("modal.delete")}
                    cancelLabel={t("modal.cancel")}
                >
                    <div className="space-y-3 py-2 text-center">
                        <p className="text-sm text-slate-600 dark:text-zinc-300 leading-relaxed">
                            {t("developments.detail.delete_attachment_desc")}
                        </p>
                        <p className="text-sm font-semibold text-slate-900 dark:text-white truncate px-2">
                            {pendingDelete?.title}
                        </p>
                    </div>
                </Modal>
            )}
        </>,
        document.body
    );
};
